/**
 * BP3 — reusable CSV bulk-import panel (Team + Fleet onboarding import).
 *
 * Flow: download template → pick CSV → server-side PREVIEW (pure, per-row
 * errors + duplicate flags shown BEFORE anything is written) → explicit
 * "Commit import" button → idempotent commit (idempotencyKey generated once
 * per preview so a retry cannot duplicate users/vehicles).
 *
 * Repo rule: template download is a BUTTON → api-client/blob, never an
 * <a href> (auth-free here, but keeps the shipped download pattern uniform).
 */
import { useRef, useState } from 'react';
import { FileDown, Upload, CheckCircle2 } from 'lucide-react';

export interface CsvImportPanelProps {
  title: string;
  template: string;
  columns: string[];
  previewFn: (csv: string) => Promise<any>;
  commitFn: (csv: string, idempotencyKey: string) => Promise<any>;
  onDone?: () => void;
}

/**
 * S2 (invite truth, Fable map claim #15): after a team import, the admin must
 * KNOW whether each invite email actually went out. Summarizes sent counts and
 * surfaces the copyable accept-invite link for any row whose email failed —
 * a missing Resend key must never mean a silently unsent invite.
 */
export function InviteTruthSummary({ results }: { results: any[] }) {
  const withInvite = (results ?? []).filter((r: any) => r.status === 'created' && r.ref?.invitePending);
  if (withInvite.length === 0) return null;
  const sent = withInvite.filter((r: any) => r.ref?.emailSent).length;
  const notSent = withInvite.filter((r: any) => r.ref?.emailSent === false);
  // Luna S2 LOW F2: rows with emailSent === undefined (older server response)
  // were silently dropped — count them honestly as unknown.
  const unknown = withInvite.length - sent - notSent.length;
  return (
    <div className="mt-2 text-xs">
      <p className="font-medium">
        Invites: {sent} email(s) sent{notSent.length ? ` · ${notSent.length} NOT sent — share these links` : ''}{unknown ? ` · ${unknown} status unknown — verify the link works` : ''}
      </p>
      {[...notSent, ...(unknown ? withInvite.filter((r: any) => r.ref?.emailSent == null) : [])].map((r: any) => (
        <div key={r.row} className="mt-1 flex items-center gap-2">
          <span className="text-gray-600">{r.ref.email}:</span>
          <code className="px-1.5 py-0.5 bg-white border border-amber-200 rounded font-mono break-all">{r.inviteUrl}</code>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(r.inviteUrl).catch(() => {})}
            className="px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Copy
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * BP3 audit L4: CSV import is an ADMIN/SUPER_ADMIN capability (the import
 * routes are requireRole('ADMIN','SUPER_ADMIN') server-side, fail-closed).
 * The UI mirrors the repo's localStorage-user role-gating pattern
 * (see AuditTemplatesPage.canEdit) so non-admins never see the import entry
 * point; the server stays the authority either way.
 */
export function canUseCsvImport(): boolean {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.role === 'ADMIN' || u?.role === 'SUPER_ADMIN';
  } catch {
    return false;
  }
}

function downloadTemplate(template: string, filename: string) {
  const blob = new Blob([template], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CsvImportPanel({ title, template, columns, previewFn, commitFn, onDone }: CsvImportPanelProps) {
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // One key per loaded preview — the commit retry that reuses it replays the
  // stored ImportJob result instead of re-inserting rows.
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  const reset = () => { setPreview(null); setResult(null); setCsv(''); setFileName(''); setError(''); idempotencyKey.current = crypto.randomUUID(); };

  const runPreview = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const res = await previewFn(csv);
      setPreview(res?.data ?? res);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not preview this CSV.');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true); setError('');
    try {
      const res = await commitFn(csv, idempotencyKey.current);
      setResult(res?.data ?? res);
      setPreview(null);
      onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  };

  const onFilePicked = async (file: File | undefined) => {
    if (!file) return;
    reset();
    setFileName(file.name);
    try {
      const text = await file.text();
      setCsv(text);
    } catch {
      setError('Could not read that file — if it came from Google Drive, download it to the device first, then pick it here.');
    }
  };

  const rows: any[] = preview?.rows ?? [];
  const hasErrors = rows.some((r) => r.errors?.length > 0 || r.duplicateOf);
  const errorRows = rows.filter((r) => r.errors?.length > 0 || r.duplicateOf);

  return (
    <div className="mb-6 bg-white rounded-xl shadow border border-gray-100 p-6" data-testid="csv-import-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => downloadTemplate(template, title.toLowerCase().includes('fleet') ? 'fleet-import-template.csv' : 'team-import-template.csv')}
            data-testid="download-template"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FileDown className="h-4 w-4" /> Download template
          </button>
          <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
            <Upload className="h-4 w-4" /> {fileName ? `File: ${fileName}` : 'Choose CSV'}
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFilePicked(e.target.files?.[0])} data-testid="csv-file-input" />
          </label>
        </div>
      </div>

      {error && <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

      {result && (
        <div className="mb-3 p-3 rounded-lg bg-green-50 text-green-700 text-sm" data-testid="import-result">
          <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4" /> Import complete</div>
          <p>{result.created} created · {result.skipped} skipped (already existed) · {result.errors} row errors</p>
          <InviteTruthSummary results={result.results ?? []} />
          {(result.results ?? []).filter((r: any) => r.status === 'error').slice(0, 10).map((r: any) => (
            <p key={r.row} className="text-xs text-red-600">Row {r.row}: {r.message}</p>
          ))}
          <button onClick={reset} className="mt-2 text-xs underline">Import another file</button>
        </div>
      )}

      {csv && !result && (
        <>
          <button
            onClick={runPreview}
            disabled={busy || !csv}
            data-testid="preview-button"
            className="mb-3 px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
          >
            {busy ? 'Checking…' : 'Check rows (preview)'}
          </button>
          {preview && (
            <div data-testid="preview-table">
              <p className="text-xs text-gray-500 mb-2">
                {rows.length} rows · {preview.errorCount} with errors · {preview.duplicateCount} duplicates.
                Nothing is saved until you commit.
              </p>
              {errorRows.length > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  {errorRows.map((r) => (
                    <p key={r.row}>Row {r.row}: {r.duplicateOf ? `${r.duplicateOf}. ` : ''}{(r.errors ?? []).join('; ') || 'would be skipped (duplicate)'}</p>
                  ))}
                </div>
              )}
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-1 pr-2">#</th>
                      {columns.map((c) => <th key={c} className="py-1 pr-2">{c}</th>)}
                      <th className="py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row} className={`border-b ${r.errors?.length ? 'bg-red-50' : r.duplicateOf ? 'bg-amber-50' : ''}`}>
                        <td className="py-1 pr-2">{r.row}</td>
                        {columns.map((c) => <td key={c} className="py-1 pr-2">{r.data?.[c]}</td>)}
                        <td className="py-1">{r.errors?.length ? r.errors.join('; ') : r.duplicateOf ? 'duplicate — will skip' : 'ready'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={commit}
                disabled={busy}
                data-testid="commit-import"
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              >
                {busy ? 'Committing…' : `Commit import (${rows.filter((r) => !r.errors?.length && !r.duplicateOf).length} rows)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
